import { moveBlock, spliceBlocks } from './ops'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { CoreState } from './state'
import { children, clean, gapPoint, plain, pointAt, rows, type Element, type Gap, type Row } from './tree'

/**
 * List item `id` alone, found by identity, as the Lexical block-actions menu treats its $draggableNode: removed (an
 * emptied list goes too), duplicated after itself, or lifted out of its list ($liftListItem) into a callout holding the
 * item's own blocks. The caret lands on what survives the action.
 */
export function listItemAction(state: CoreState, id: string, action: 'delete' | 'duplicate' | 'callout',
  copy: (block: Block) => Block): void {
  const path = ancestry(state.blocks, id)
  const item = path?.at(-1)
  const list = path?.at(-2)
  if (!path || item?.type !== 'li' || list?.type !== 'list') return
  const order = treeOrder(state.blocks)
  const from = order.indexOf(item.id)
  const to = from + treeOrder([item]).length - 1
  const entries = rows(state.blocks)
  const before = entries.findLast((entry) => order.indexOf(entry.block.id) < from)
  const after = entries.find((entry) => order.indexOf(entry.block.id) > to)
  const caret = (entry: Row | undefined, end: boolean, fallback: Gap) => {
    const point = entry ? pointAt(entry, end ? plain(entry.block).length : 0) : gapPoint(fallback)
    state.selection = { anchor: point, focus: point }
  }
  if (action === 'delete') {
    spliceBlocks(state, list.children, list.children.indexOf(item), 1)
    clean(state, state.blocks)
    caret(before ?? after, !!before, { siblings: state.blocks, index: 0 })
  } else if (action === 'duplicate') {
    const duplicate = copy(item)
    spliceBlocks(state, list.children, list.children.indexOf(item) + 1, 0, duplicate)
    caret(rows([duplicate])[0], true, { siblings: children(duplicate), parent: duplicate, index: 0 })
  } else {
    const top = path.find((block) => block.type === 'list') ?? list
    const parent = path[path.indexOf(top) - 1] as Element | undefined
    const outer = parent ? parent.children : state.blocks
    const at = list.children.indexOf(item)
    const owned = item.children.filter((child) => child.type === 'list').flatMap((child) => children(child))
    const content = item.children.filter((child) => child.type !== 'list')
    const callout: Element = { id: '', type: 'alert', icon: '💡', color: 'info',
      children: content.length > 0 ? content : [{ id: '', type: 'p', children: [] }] }
    spliceBlocks(state, list.children, at, 1, ...owned)
    const topAt = outer.indexOf(top)
    if (list !== top) spliceBlocks(state, outer, topAt + 1, 0, callout)
    else if (at === 0) spliceBlocks(state, outer, topAt, 0, callout)
    else {
      const trailing = spliceBlocks(state, list.children, at, list.children.length - at)
      spliceBlocks(state, outer, topAt + 1, 0, callout, ...(trailing.length > 0 ? [{ ...list, id: '', children: trailing }] : []))
    }
    clean(state, state.blocks)
    caret(rows([callout])[0], true, { siblings: callout.children, parent: callout, index: 0 })
  }
}

/** The blocks from the document down to block `id`, that block last. */
function ancestry(items: Blocks, id: string): Block[] | undefined {
  for (const block of items) {
    if (block.id === id) return [block]
    const below = ancestry(children(block), id)
    if (below) return [block, ...below]
  }
  return undefined
}

/** Every block id in document order. */
function treeOrder(items: Blocks): string[] {
  return items.flatMap((block) => [block.id, ...treeOrder(children(block))])
}

export function indentItem(state: CoreState, row: Row, outdent: boolean): void {
  const item = row.parents.findLast((parent) => parent.type === 'li')
  const list = row.parents.findLast((parent) => parent.type === 'list')
  if (!item || !list || item.children[0] !== row.block) {
    if (outdent && item && list && row.parents.at(-1) === item) {
      // A later row leaves its item one level, in place (ProseMirror lift): the item and its list split around it, the
      // item's blocks after the row and the list's later items continuing in a new item and list after it.
      const parent = row.parents[row.parents.indexOf(list) - 1]
      const siblings = parent ? parent.children : state.blocks
      const at = item.children.indexOf(row.block)
      const rest = spliceBlocks(state, item.children, at + 1, item.children.length - at - 1)
      const following = spliceBlocks(state, list.children, list.children.indexOf(item) + 1,
        list.children.length - list.children.indexOf(item) - 1)
      spliceBlocks(state, item.children, at, 1)
      const items = [...rest.length > 0 ? [{ ...item, id: '', children: rest } as Element] : [], ...following]
      spliceBlocks(state, siblings, siblings.indexOf(list) + 1, 0, row.block,
        ...items.length > 0 ? [{ ...list, id: '', children: items } as Element] : [])
    } else if (!outdent) {
      const previous = row.siblings[row.siblings.indexOf(row.block) - 1]
      const owner = previous?.type === 'list' ? children(previous).at(-1) : previous
      if (owner?.type === 'li') {
        moveBlock(state, row.block, owner.children, owner.children.length)
      }
    }
    clean(state, state.blocks)
    return
  }
  const at = list.children.indexOf(item)
  if (outdent) {
    const owner = row.parents[row.parents.indexOf(list) - 1]
    const outer = row.parents[row.parents.indexOf(list) - 2]
    if (owner?.type !== 'li' || outer?.type !== 'list') {
      return
    }
    // The outdented item takes what followed it, in order: its list's later items, then its owner's blocks after that
    // list (Notion unindent adopts the following siblings; ProseMirror liftToOuterList never reorders).
    const following = spliceBlocks(state, list.children, at + 1, list.children.length - at - 1)
    const trailing = spliceBlocks(state, owner.children, owner.children.indexOf(list) + 1,
      owner.children.length - owner.children.indexOf(list) - 1)
    spliceBlocks(state, list.children, at, 1)
    if (following.length > 0) {
      const nested = item.children.at(-1)
      if (nested?.type === 'list' && nested.subtype === list.subtype) {
        spliceBlocks(state, nested.children, nested.children.length, 0, ...following)
      } else spliceBlocks(state, item.children, item.children.length, 0,
        { ...list, id: '', children: following })
    }
    if (trailing.length > 0) spliceBlocks(state, item.children, item.children.length, 0, ...trailing)
    spliceBlocks(state, outer.children, outer.children.indexOf(owner) + 1, 0, item)
    if (list.children.length === 0) {
      spliceBlocks(state, owner.children, owner.children.indexOf(list), 1)
    }
  } else {
    const previous = list.children[at - 1]
    if (previous?.type !== 'li') return
    let nested = previous.children.at(-1)
    // Tab nests the item alone: its nested list of the same kind becomes the previous item's, the item first in it, so the
    // item's children end up its siblings (superseded 2026-10-30 by Notion browser check, docs/notion-ux/decisions.md D7:
    // `bContainsC:false, aContainsC:true`). The list moves whole, keeping a peer's item in it. Observed only when the
    // previous item had no nested list among its blocks and for a non-toggle list.
    const own = item.children.length === 2 ? item.children[1] : undefined
    if (!previous.children.some((block) => block.type === 'list') && own?.type === 'list' && own.subtype === list.subtype && list.subtype !== 'toggle') {
      moveBlock(state, own, previous.children, previous.children.length)
      moveBlock(state, item, children(own), 0)
    } else {
      if (nested?.type !== 'list' || nested.subtype !== list.subtype) {
        nested = { ...list, id: '', children: [] }
        spliceBlocks(state, previous.children, previous.children.length, 0, nested)
      }
      moveBlock(state, item, children(nested), children(nested).length)
    }
  }
  clean(state, state.blocks)
}
